/**
 * W2C-1.5 — Messaging Routes admin panel.
 *
 * Manage approved Twilio Messaging Service SIDs (SMS) and SendGrid
 * from-identities (email) as named routes. Client-workflow templates
 * link to a route so the executor (W2C-2) can resolve the correct
 * MessagingServiceSid / from-email at send time.
 *
 * This panel only manages DEFINITIONS — no SMS/email is sent from here.
 */
import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Power, PowerOff, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PROVIDER_LABELS = { twilio: "Twilio", sendgrid: "SendGrid" };
const CHANNEL_LABELS = { sms: "SMS", email: "Email" };
const AUDIENCE_LABELS = { lead: "Leads", client: "Clients", both: "Both" };

export default function MessagingRoutesPanel() {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setRoutes((await api.get("/messaging-routes")).data || []); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const activate = async (r) => {
        try {
            await api.patch(`/messaging-routes/${r.id}/${r.active ? "deactivate" : "activate"}`);
            toast.success(r.active ? "Route deactivated" : "Route activated");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (r) => {
        try {
            await api.delete(`/messaging-routes/${r.id}`);
            toast.success("Route deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="messaging-routes-panel">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-emerald-600" /> Approved Messaging Routes
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Add each approved Twilio Messaging Service SID (or SendGrid identity) as its own route. Attach templates to a route so the workflow executor knows which SID to send from.
                    </div>
                </div>
                <Button size="sm" onClick={() => setEditing({})} disabled={!!editing} data-testid="add-messaging-route-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add route
                </Button>
            </div>

            {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
                <div className="space-y-2">
                    {routes.length === 0 && !editing && (
                        <div className="text-xs text-muted-foreground italic border border-dashed border-slate-200 rounded px-3 py-3">
                            No messaging routes configured yet. Add one to start routing approved client-workflow templates through the correct Twilio Messaging Service SID.
                        </div>
                    )}
                    {routes.map(r => (
                        <RouteRow
                            key={r.id}
                            route={r}
                            onEdit={() => setEditing(r)}
                            onToggleActive={() => activate(r)}
                            onDelete={() => setConfirmDel(r)}
                            disabled={!!editing}
                        />
                    ))}
                    {editing !== null && (
                        <RouteEditor
                            initial={editing?.id ? editing : null}
                            onCancel={() => setEditing(null)}
                            onSaved={async () => { setEditing(null); await load(); }}
                        />
                    )}
                </div>
            )}

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
                <AlertDialogContent data-testid="confirm-delete-route-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete messaging route?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the route. Templates already linked to this route will lose their route assignment. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-route-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-rose-600 hover:bg-rose-700"
                            onClick={async () => { const r = confirmDel; setConfirmDel(null); if (r) await del(r); }}
                            data-testid="confirm-route-delete"
                        >Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function RouteRow({ route, onEdit, onToggleActive, onDelete, disabled }) {
    const provider = PROVIDER_LABELS[route.provider] || route.provider;
    const channel = CHANNEL_LABELS[route.channel] || route.channel;
    const audience = AUDIENCE_LABELS[route.audience] || route.audience;
    const sidLabel = route.channel === "sms"
        ? (route.twilio_messaging_service_sid ? `SID ${route.twilio_messaging_service_sid.slice(0, 6)}…${route.twilio_messaging_service_sid.slice(-4)}` : (route.twilio_from_number || "no target"))
        : (route.sendgrid_from_email || "no from-email");
    return (
        <div
            className={`rounded-lg border ${route.active ? "border-border bg-white" : "border-slate-200 bg-slate-50/60"} shadow-sm px-3 py-2 flex flex-wrap items-center gap-3`}
            data-testid={`messaging-route-row-${route.id}`}
        >
            <div className="flex-1 min-w-[240px]">
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {route.name}
                    {route.is_default && <span className="text-[10px] uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full px-1.5 py-0.5">default</span>}
                    {route.approved && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5">approved</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                    {provider} · {channel} · {audience}
                    {route.purpose ? ` · ${route.purpose}` : ""}
                    <span className="ml-2 font-mono text-[11px]">{sidLabel}</span>
                </div>
                {route.description && <div className="text-xs text-gray-600 mt-0.5">{route.description}</div>}
                {!route.active && <div className="text-[10.5px] uppercase tracking-wider text-rose-700 font-semibold mt-0.5">Deactivated</div>}
            </div>
            <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={disabled} onClick={onEdit} data-testid={`edit-messaging-route-${route.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" disabled={disabled} onClick={onToggleActive} data-testid={`toggle-messaging-route-${route.id}`}>
                    {route.active ? <PowerOff className="h-3.5 w-3.5 mr-1" /> : <Power className="h-3.5 w-3.5 mr-1" />}
                    {route.active ? "Deactivate" : "Activate"}
                </Button>
                <Button size="sm" variant="ghost" disabled={disabled} onClick={onDelete} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-messaging-route-${route.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
            </div>
        </div>
    );
}

function RouteEditor({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(() => ({
        name: initial?.name || "",
        description: initial?.description || "",
        channel: initial?.channel || "sms",
        provider: initial?.provider || "twilio",
        audience: initial?.audience || "client",
        purpose: initial?.purpose || "client_workflow",
        active: initial?.active ?? true,
        approved: initial?.approved ?? true,
        is_default: initial?.is_default ?? false,
        twilio_messaging_service_sid: initial?.twilio_messaging_service_sid || "",
        twilio_from_number: initial?.twilio_from_number || "",
        sendgrid_from_email: initial?.sendgrid_from_email || "",
        sendgrid_from_name: initial?.sendgrid_from_name || "",
        sendgrid_reply_to: initial?.sendgrid_reply_to || "",
        compliance_notes: initial?.compliance_notes || "",
    }));
    const [busy, setBusy] = useState(false);

    const isValid = (() => {
        if (!(form.name || "").trim()) return false;
        if (form.channel === "sms" && form.provider === "twilio") {
            const sid = (form.twilio_messaging_service_sid || "").trim();
            const frm = (form.twilio_from_number || "").trim();
            if (!sid && !frm) return false;
            if (sid && !sid.startsWith("MG")) return false;
        }
        if (form.channel === "email" && form.provider === "sendgrid") {
            if (!(form.sendgrid_from_email || "").trim()) return false;
        }
        return true;
    })();

    const save = async () => {
        setBusy(true);
        try {
            if (initial?.id) await api.put(`/messaging-routes/${initial.id}`, form);
            else await api.post("/messaging-routes", form);
            toast.success(initial?.id ? "Route updated" : "Route created");
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3 space-y-3" data-testid="messaging-route-editor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Name">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder="e.g. Client Workflow General" data-testid="route-name" />
                </F>
                <F label="Purpose (freeform)">
                    <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                           placeholder="client_workflow | client_poi | client_renewal" data-testid="route-purpose" />
                </F>
                <F label="Channel">
                    <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v, provider: v === "email" ? "sendgrid" : "twilio" })}>
                        <SelectTrigger data-testid="route-channel"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                    </Select>
                </F>
                <F label="Audience">
                    <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                        <SelectTrigger data-testid="route-audience"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="lead">Leads</SelectItem>
                            <SelectItem value="client">Clients</SelectItem>
                            <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                    </Select>
                </F>
                {form.channel === "sms" && (
                    <>
                        <F label="Twilio Messaging Service SID">
                            <Input value={form.twilio_messaging_service_sid}
                                   onChange={(e) => setForm({ ...form, twilio_messaging_service_sid: e.target.value })}
                                   placeholder="MG…" data-testid="route-sid" />
                        </F>
                        <F label="Twilio From Number (fallback)">
                            <Input value={form.twilio_from_number}
                                   onChange={(e) => setForm({ ...form, twilio_from_number: e.target.value })}
                                   placeholder="+1…" data-testid="route-from-number" />
                        </F>
                    </>
                )}
                {form.channel === "email" && (
                    <>
                        <F label="SendGrid From Email">
                            <Input value={form.sendgrid_from_email}
                                   onChange={(e) => setForm({ ...form, sendgrid_from_email: e.target.value })}
                                   placeholder="hello@aplusstudentloans.us" data-testid="route-from-email" />
                        </F>
                        <F label="SendGrid From Name">
                            <Input value={form.sendgrid_from_name}
                                   onChange={(e) => setForm({ ...form, sendgrid_from_name: e.target.value })}
                                   placeholder="A+ Student Loans" data-testid="route-from-name" />
                        </F>
                    </>
                )}
                <F label="Active">
                    <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                        <SelectTrigger data-testid="route-active"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                    </Select>
                </F>
                <F label="Approved">
                    <Select value={form.approved ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, approved: v === "yes" })}>
                        <SelectTrigger data-testid="route-approved"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                    </Select>
                </F>
            </div>
            <F label="Description (optional)">
                <Input value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="Short label displayed alongside the route." data-testid="route-description" />
            </F>
            <F label="Compliance notes (optional)">
                <Textarea rows={2} value={form.compliance_notes}
                          onChange={(e) => setForm({ ...form, compliance_notes: e.target.value })}
                          placeholder="Approved copy references, campaign registration ID, etc." data-testid="route-compliance-notes" />
            </F>
            <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="route-editor-cancel">Cancel</Button>
                <Button onClick={save} disabled={busy || !isValid} data-testid="route-editor-save">
                    {busy ? "Saving…" : initial?.id ? "Save" : "Add route"}
                </Button>
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">{label}</div>
            {children}
        </label>
    );
}
